/**
 * SpryBoard REST Backend (SpryWorkspace)
 *
 * Replaces the legacy api.sprymeeting.com + Firebase backend.
 * Uses spryworkspace-backend via axios (api/backend.ts).
 * No more client-side encryption — the server stores data in plaintext.
 */
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";
import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";
import {
  boardAPI,
  spryboardAPI,
  getAccessToken,
  extractBoardFromResponse,
} from "../api/backend";

// =============================================================================
// Scene Version Cache
// =============================================================================

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToBackend = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  return true;
};

// =============================================================================
// File Upload/Download (Binary assets like images)
// =============================================================================

const API_URL =
  import.meta.env.VITE_APP_API_URL ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:3001/api";

const getSpryboardContextIdFromUrl = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const boardId = params.get("boardId")?.trim();
    const fileId = params.get("fileId")?.trim();
    return boardId || fileId || null;
  } catch {
    return null;
  }
};

const extractSpryboardContextId = (prefix: string): string | null => {
  const routeContextId = getSpryboardContextIdFromUrl();
  if (routeContextId) {
    return routeContextId;
  }
  if (!prefix) {
    return null;
  }
  if (!prefix.includes("/")) {
    return prefix;
  }
  const roomMatch = prefix.match(/(?:^|\/)rooms\/([^/]+)$/);
  return roomMatch?.[1] || null;
};

export const saveFilesToBackend = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  const token = getAccessToken();
  const boardContextId = extractSpryboardContextId(prefix) || prefix;

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        // Determine MIME type from buffer (check for common image signatures)
        let mimeType = "image/png";
        const header = buffer.slice(0, 4);
        const headerStr = Array.from(header).map(b => b.toString(16).padStart(2, '0')).join('');
        if (headerStr.startsWith('89504e47')) {
          mimeType = 'image/png';
        } else if (headerStr.startsWith('ffd8')) {
          mimeType = 'image/jpeg';
        } else if (headerStr.startsWith('47494638')) {
          mimeType = 'image/gif';
        } else if (headerStr.startsWith('52494646') && buffer[8] === 0x57 && buffer[9] === 0x45) {
          mimeType = 'image/webp';
        }

        const blob = new Blob([buffer], { type: mimeType });
        const formData = new FormData();
        formData.append("image", blob, `${id}.bin`);
        formData.append("boardId", boardContextId);
        formData.append("fileId", id); // Pass the Excalidraw fileId to use as the DriveFile ID

        // Use the dedicated spryboard image upload endpoint
        const response = await fetch(`${API_URL}/spryboard/images/upload`, {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        });

        if (response.ok) {
          savedFiles.push(id);
        } else {
          console.error("Failed to upload file", id, response.statusText);
          erroredFiles.push(id);
        }
      } catch (error) {
        console.error(error);
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromBackend = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();
  const contextId = extractSpryboardContextId(prefix);

  console.log('[SpryBoard] loadFilesFromBackend called with:', { prefix, fileIds: filesIds });

  // In SpryWorkspace mode, files are stored unencrypted in MinIO/DB.
  // The decryptionKey parameter is kept for interface compatibility but not used.

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        console.log('[SpryBoard] Loading image file:', id);
        
        // Use the dedicated spryboard image endpoint to get presigned URL
        const imageEndpoint = new URL(`${API_URL}/spryboard/images/${id}`);
        if (contextId) {
          imageEndpoint.searchParams.set("contextId", contextId);
        }

        const response = await fetch(
          imageEndpoint.toString(),
          {
            headers: {
              ...(getAccessToken()
                ? { Authorization: `Bearer ${getAccessToken()}` }
                : {}),
            },
          },
        );

        console.log('[SpryBoard] Image response status:', response.status, 'for file:', id);

        if (response.status < 400) {
          const apiPayload = await response.json();
          const presignedUrl = apiPayload?.data?.url;

          if (!presignedUrl) {
            console.error('[SpryBoard] No presigned URL in response:', apiPayload);
            erroredFiles.set(id, true);
            return;
          }

          console.log('[SpryBoard] Got presigned URL for file:', id);

          // Fetch the actual image from presigned URL
          const imageResponse = await fetch(presignedUrl);
          if (!imageResponse.ok) {
            console.error('[SpryBoard] Failed to fetch image from presigned URL:', imageResponse.status);
            erroredFiles.set(id, true);
            return;
          }

          const arrayBuffer = await imageResponse.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);

          if (decryptionKey) {
            try {
              const { data, metadata } = await decompressData<BinaryFileMetadata>(
                bytes,
                { decryptionKey },
              );

              loadedFiles.push({
                mimeType: metadata?.mimeType || MIME_TYPES.binary,
                id,
                dataURL: new TextDecoder().decode(data) as DataURL,
                created: metadata?.created || Date.now(),
                lastRetrieved: Date.now(),
              });
              return;
            } catch (decodeError) {
              console.warn(
                "[SpryBoard] File was not encoded payload, falling back to raw image decode:",
                id,
                decodeError,
              );
            }
          }

          const mimeType =
            apiPayload?.data?.mimeType ||
            imageResponse.headers.get("content-type") ||
            MIME_TYPES.binary;
          const blob = new Blob([arrayBuffer], { type: mimeType });
          const reader = new FileReader();
          const dataURL = await new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });

          loadedFiles.push({
            mimeType,
            id,
            dataURL: dataURL as DataURL,
            created: Date.now(),
            lastRetrieved: Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};

// =============================================================================
// Scene Save/Load (for collaboration room persistence)
// =============================================================================

export const saveToBackend = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToBackend(portal, elements)
  ) {
    return null;
  }

  try {
    // Save to SpryWorkspace backend (no encryption needed)
    const boardData = {
      elements: Array.from(elements),
      appState: {
        viewBackgroundColor: appState.viewBackgroundColor,
        gridSize: appState.gridSize,
      },
    };

    // Use boardId as roomId if it's a board collaboration room
    await boardAPI.updateBoard(roomId, {
      data: boardData,
    });

    const storedElements = getSyncableElements(
      restoreElements(elements, null),
    );
    SceneVersionCache.set(socket, storedElements);
    return storedElements;
  } catch (error) {
    console.error("Error saving to backend", error);
    return null;
  }
};

export const loadFromBackend = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  try {
    const response = await boardAPI.getBoard(roomId, { useDrive: true });
    const boardPayload = extractBoardFromResponse(response);
    const boardData = boardPayload?.data ?? null;

    if (!boardData?.elements) {
      return null;
    }

    const elements = getSyncableElements(
      restoreElements(boardData.elements, null, {
        deleteInvisibleElements: true,
      }),
    );

    if (socket) {
      SceneVersionCache.set(socket, elements);
    }

    return elements;
  } catch (error) {
    console.error("Error loading from backend", error);
    return null;
  }
};
